import { usePortfolio } from '../../lib/hooks';
import { Github, Linkedin, Mail, FileText } from 'lucide-react';
import defaultAvatar from '../../assets/avatar.png';

const navItems = [
  { id: 'home', num: '01', label: 'HOME' },
  { id: 'projects', num: '02', label: 'PROJECTS' },
  { id: 'stack', num: '03', label: 'STACK' },
  { id: 'testimonials', num: '04', label: 'TESTIMONIALS' },
];

const ProfileSidebar = ({ activeSection, onNavigate }) => {
  const { profile, loading } = usePortfolio();

  if (loading) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <div className="font-mono text-xs uppercase tracking-widest text-[#8B8B93]">
          LOADING_PROFILE...
        </div>
      </div>
    );
  }

  const name = profile?.name || 'Sonjay E. Cimanes';
  const role =
    profile?.title && !['Versatile Engineer', 'Applied AI Developer'].includes(profile.title)
      ? profile.title
      : 'Software Engineer';
  const image = profile?.image || defaultAvatar;
  const github = profile?.github || 'https://github.com/sanjie01';
  const linkedin = profile?.linkedin || 'https://www.linkedin.com/in/sonjay-cimanes-2966b1372';
  const email = profile?.email || 'cimanes.sj@gmail.com';
  const resumeUrl = profile?.resumeUrl || profile?.resume_url || '/Sonjay_Cimanes_Resume.pdf';

  const initials = name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase();

  return (
    <div className="mx-auto flex h-full w-full max-w-sm flex-col justify-between px-0 py-5 sm:px-2 sm:py-6 lg:max-h-[740px] lg:py-4">
      {/* 1. Header: Photo + Name + Title */}
      <div className="flex flex-col items-center lg:items-start">
        {/* Circle for Photo */}
        <div className="relative group">
          <div className="w-24 h-24 sm:w-28 sm:h-28 lg:w-32 lg:h-32 rounded-full overflow-hidden border-2 border-[#27272A] shadow-xl ring-4 ring-[#22C55E]/15 bg-[#18181B]">
            {image ? (
              <img
                src={image}
                alt={name}
                className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-300"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-[#18181B]">
                <span className="font-mono text-3xl lg:text-4xl font-bold text-[#EDEDEF] tracking-wider">
                  {initials}
                </span>
              </div>
            )}
          </div>
          <span
            className="absolute bottom-1 right-1 w-4 h-4 rounded-full bg-[#22C55E] border-2 border-[#0A0A0B] ring-1 ring-[#22C55E]/40"
            title="Available for work"
          />
        </div>

        {/* Metric Label: Availability */}
        <div className="mt-3.5 inline-flex items-center gap-2 font-mono text-[11px] tracking-wider text-[#8B8B93]">
          <span className="w-2 h-2 rounded-full bg-[#22C55E] animate-pulse" />
          <span>STATUS:</span>
          <span className="text-[#22C55E] font-medium">AVAILABLE</span>
        </div>

        {/* Name in Monospace Accent */}
        <h1 className="mt-3 text-center text-2xl font-mono font-bold tracking-tight text-[#EDEDEF] sm:text-3xl lg:text-left">
          {name}
        </h1>

        {/* Title in clean geometric sans */}
        <p className="mt-1 text-center text-sm font-medium tracking-normal text-[#8B8B93] sm:text-base lg:text-left">
          {role}
        </p>
      </div>

      {/* 2. Vertical Navigation */}
      <nav className="scrollbar-hide my-6 w-full sm:my-8 sm:overflow-x-auto lg:overflow-visible" aria-label="Portfolio navigation">
        <ul className="grid w-full grid-cols-2 gap-2 sm:flex sm:min-w-max lg:min-w-0 lg:flex-col lg:space-y-2">
          {navItems.map((item) => {
            const isActive = activeSection === item.id;
            return (
              <li key={item.id}>
                <button
                  type="button"
                  onClick={() => onNavigate(item.id)}
                  className={`flex w-full items-center whitespace-nowrap rounded-lg border-b-2 px-3 py-2.5 text-left transition-all duration-200 cursor-pointer sm:w-auto lg:w-full lg:border-b-0 lg:border-l-4 lg:px-4 ${isActive
                    ? 'border-b-[#22C55E] bg-[#18181B] font-semibold text-[#EDEDEF] shadow-sm lg:border-b-transparent lg:border-l-[#22C55E]'
                    : 'border-b-transparent font-medium text-[#8B8B93] hover:bg-[#141416] hover:text-[#EDEDEF] lg:border-l-transparent'
                    }`}
                >
                  <span
                    className={`font-mono text-xs mr-2 select-none sm:mr-3 ${isActive ? 'text-[#4ADE80]' : 'text-[#8B8B93]/60'
                      }`}
                  >
                    {item.num} /
                  </span>
                  <span className="font-mono text-xs tracking-widest uppercase font-semibold">
                    {item.label}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* 3. Bottom Area: Social Icons + Resume */}
      <div className="flex flex-col space-y-5 pt-2 border-t border-[#27272A]">
        {/* Socials: GitHub, LinkedIn, Mail */}
        <div className="flex items-center gap-3">
          {github && (
            <a
              href={github}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center w-10 h-10 rounded-full border border-[#27272A] bg-[#141416] text-[#8B8B93] hover:text-[#22C55E] transition-colors shadow-sm"
              aria-label="GitHub"
              title="GitHub"
            >
              <Github size={18} />
            </a>
          )}

          {linkedin && (
            <a
              href={linkedin}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center w-10 h-10 rounded-full border border-[#27272A] bg-[#141416] text-[#8B8B93] hover:text-[#22C55E] transition-colors shadow-sm"
              aria-label="LinkedIn"
              title="LinkedIn"
            >
              <Linkedin size={18} />
            </a>
          )}

          {email && (
            <a
              href={`mailto:${email}`}
              className="inline-flex items-center justify-center w-10 h-10 rounded-full border border-[#27272A] bg-[#141416] text-[#8B8B93] hover:text-[#22C55E] transition-colors shadow-sm"
              aria-label="Email"
              title={`Email ${email}`}
            >
              <Mail size={18} />
            </a>
          )}
        </div>

        {/* Resume */}
        <div className="flex items-center gap-3">
          <a
            href={resumeUrl || '#cta'}
            target="_blank"
            rel="noopener noreferrer"
            download={false}
            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-[#27272A] bg-[#141416] hover:border-[#22C55E]/60 text-[#EDEDEF] hover:text-[#22C55E] hover:bg-[#22C55E]/5 font-mono text-xs font-semibold tracking-wider uppercase transition-all active:scale-98"
          >
            <FileText size={15} />
            <span>View Resume</span>
          </a>
        </div>
      </div>
    </div>
  );
};

export default ProfileSidebar;
